import { javaIsBlank, javaStrip } from '../javaText.ts';
import { writeJson, type JsonValue } from '../jsonWriter.ts';
import { InterchangeError, JavaNumber, javaValueOf, readJavaJson, type JavaJson } from '../tables/jsonReader.ts';
import {
  SHEET_ROLES,
  isBuiltIn,
  keyFor,
  sheetField,
  sheetSection,
  type CharacterSheet,
  type SheetField,
  type SheetFieldType,
  type SheetRole,
  type SheetRoles,
  type SheetSection,
  type SheetTemplate,
} from './sheetModel.ts';

/**
 * JSON for character sheets, as the desktop's `sheets.SheetCodec` writes and reads it, in
 * three shapes:
 * - a template's definition (its sections, roles and conditions), which is how a realm
 *   stores it, in `sheet_templates.definition`;
 * - a template export, `"format": "realmery-sheet-template"`;
 * - a sheet export, `"format": "realmery-character-sheet"`, with its template inside, so a
 *   sheet imports into any realm.
 * Reading is lenient about what it doesn't need and strict about what it does, with the
 * desktop's messages (schema/fixtures/sheet-cases.json).
 */

export const TEMPLATE_FORMAT_ID = 'realmery-sheet-template';
export const SHEET_FORMAT_ID = 'realmery-character-sheet';
const FORMAT_VERSION = 1;

/** A template as read from a file, before it has an id in any realm. */
export interface TemplateData {
  readonly name: string;
  readonly system: string;
  readonly description: string;
  readonly license: string;
  readonly builtinKey: string;
  readonly sections: readonly SheetSection[];
  readonly roles: SheetRoles;
  readonly conditions: readonly string[];
}

/** A sheet as read from a file: its template, its name, and what's written in it. */
export interface SheetData {
  readonly template: TemplateData;
  readonly name: string;
  readonly values: ReadonlyMap<string, string>;
}

export interface Definition {
  readonly sections: readonly SheetSection[];
  readonly roles: SheetRoles;
  readonly conditions: readonly string[];
}

// --- what a realm stores ---

export function writeDefinition(sections: readonly SheetSection[], roles: SheetRoles, conditions: readonly string[]): string {
  const root = new Map<string, JsonValue>([['roles', rolesJson(roles)]]);
  if (conditions.length > 0) root.set('conditions', [...conditions]);
  root.set('sections', sectionsJson(sections));
  return writeJson(root);
}

export function readDefinition(json: string): Definition {
  const map = readJavaJson(json);
  if (!(map instanceof Map)) throw new InterchangeError('A template definition must be a JSON object.');
  return { sections: readSections(map), roles: readRoles(map), conditions: readConditions(map) };
}

export function writeValues(values: ReadonlyMap<string, string>): string {
  return writeJson(new Map(values));
}

export function readValues(json: string | null): Map<string, string> {
  const values = new Map<string, string>();
  if (json === null || javaIsBlank(json)) return values;
  const map = readJavaJson(json);
  if (map instanceof Map) for (const [key, value] of map) if (value !== null) values.set(key, javaValueOf(value));
  return values;
}

// --- export and import ---

export function writeTemplate(template: SheetTemplate): string {
  return writeJson(
    new Map<string, JsonValue>([
      ['format', TEMPLATE_FORMAT_ID],
      ['version', FORMAT_VERSION],
      ...templateJson(template),
    ]),
  );
}

export function writeSheet(sheet: CharacterSheet, template: SheetTemplate): string {
  return writeJson(
    new Map<string, JsonValue>([
      ['format', SHEET_FORMAT_ID],
      ['version', FORMAT_VERSION],
      ['name', sheet.name],
      ['values', new Map(sheet.values)],
      ['template', templateJson(template)],
    ]),
  );
}

/** A template export, or the template inside a sheet export, so either file works. */
export function readTemplate(json: string): TemplateData {
  const map = object(json);
  const inner = map.get('template');
  if (map.get('format') === SHEET_FORMAT_ID && inner instanceof Map) return templateFrom(inner);
  if (!map.has('sections')) throw new InterchangeError(`This file isn't a Realmery sheet template - it has no "sections".`);
  return templateFrom(map);
}

export function readSheet(json: string): SheetData {
  const map = object(json);
  const templateMap = map.get('template');
  if (!(templateMap instanceof Map)) throw new InterchangeError(`This file isn't a Realmery character sheet - it has no "template".`);
  const values = new Map<string, string>();
  const valueMap = map.get('values');
  if (valueMap instanceof Map) for (const [key, value] of valueMap) if (value !== null) values.set(key, javaValueOf(value));
  return { template: templateFrom(templateMap), name: text(map, 'name'), values };
}

/** Which of the two export formats `json` is, or null if neither. */
export function formatOf(json: string): typeof SHEET_FORMAT_ID | typeof TEMPLATE_FORMAT_ID | null {
  try {
    const map = object(json);
    const format = map.get('format');
    if (format === SHEET_FORMAT_ID || format === TEMPLATE_FORMAT_ID) return format;
    return map.has('template') ? SHEET_FORMAT_ID : map.has('sections') ? TEMPLATE_FORMAT_ID : null;
  } catch (e) {
    if (e instanceof InterchangeError) return null;
    throw e;
  }
}

// --- shared ---

function templateJson(template: SheetTemplate): Map<string, JsonValue> {
  const map = new Map<string, JsonValue>([
    ['name', template.name],
    ['system', template.system],
    ['description', template.description],
    ['license', template.license],
  ]);
  if (isBuiltIn(template)) map.set('builtinKey', template.builtinKey);
  map.set('roles', rolesJson(template.roles));
  if (template.conditions.length > 0) map.set('conditions', [...template.conditions]);
  map.set('sections', sectionsJson(template.sections));
  return map;
}

/** A template's JSON object, already read: a template export, or the template inside a sheet. */
export function templateFrom(map: ReadonlyMap<string, JavaJson>): TemplateData {
  return {
    name: text(map, 'name'),
    system: text(map, 'system'),
    description: text(map, 'description'),
    license: text(map, 'license'),
    builtinKey: text(map, 'builtinKey'),
    sections: readSections(map),
    roles: readRoles(map),
    conditions: readConditions(map),
  };
}

function rolesJson(roles: SheetRoles): Map<string, JsonValue> {
  const map = new Map<string, JsonValue>();
  for (const role of SHEET_ROLES) {
    const key = roles[role];
    if (key !== undefined) map.set(role.toLowerCase(), key);
  }
  return map;
}

function sectionsJson(sections: readonly SheetSection[]): JsonValue[] {
  return sections.map(
    (section) =>
      new Map<string, JsonValue>([
        ['title', section.title],
        ['columns', section.columns],
        ['fields', section.fields.map(fieldJson)],
      ]),
  );
}

function fieldJson(field: SheetField): Map<string, JsonValue> {
  const map = new Map<string, JsonValue>([
    ['key', field.key],
    ['label', field.label],
    ['type', field.type.toLowerCase()],
  ]);
  if (field.defaultValue !== '') map.set('default', field.defaultValue);
  if (field.formula !== '') map.set('formula', field.formula);
  if (field.roll !== '') map.set('roll', field.roll);
  if (field.options.length > 0) map.set('options', [...field.options]);
  if (field.help !== '') map.set('help', field.help);
  return map;
}

const FIELD_TYPE_NAMES = new Set<string>(['NUMBER', 'TEXT', 'LONG_TEXT', 'CHECKBOX', 'CHOICE', 'RESOURCE', 'DERIVED', 'DICE']);

function readSections(map: ReadonlyMap<string, JavaJson>): SheetSection[] {
  const list = map.get('sections');
  if (!Array.isArray(list)) throw new InterchangeError('A sheet template needs a "sections" list.');
  const sections: SheetSection[] = [];
  for (const item of list) {
    if (!(item instanceof Map)) continue;
    const fields: SheetField[] = [];
    const fieldList = item.get('fields');
    if (Array.isArray(fieldList)) for (const fieldItem of fieldList) if (fieldItem instanceof Map) fields.push(readField(fieldItem));
    const columns = item.get('columns');
    sections.push(sheetSection(text(item, 'title'), columns instanceof JavaNumber ? intValue(columns) : 2, fields));
  }
  return sections;
}

/** `Number.intValue()`: a long's low 32 bits; a double cut toward zero, within an int's range. */
function intValue(number: JavaNumber): number {
  if (number.isLong) return Number(BigInt.asIntN(32, number.long));
  if (Number.isNaN(number.value)) return 0;
  return Math.trunc(Math.max(-(2 ** 31), Math.min(2 ** 31 - 1, number.value)));
}

function readField(map: ReadonlyMap<string, JavaJson>): SheetField {
  const typeName = text(map, 'type').toUpperCase();
  const type = (FIELD_TYPE_NAMES.has(typeName) ? typeName : 'TEXT') as SheetFieldType;
  const options: string[] = [];
  const list = map.get('options');
  if (Array.isArray(list)) for (const option of list) if (option !== null) options.push(javaValueOf(option));
  const key = text(map, 'key');
  const label = text(map, 'label');
  return sheetField(javaIsBlank(key) ? keyFor(label) : key, label, type, text(map, 'default'), text(map, 'formula'), text(map, 'roll'), options, text(map, 'help'));
}

function readConditions(map: ReadonlyMap<string, JavaJson>): string[] {
  const conditions: string[] = [];
  const list = map.get('conditions');
  if (Array.isArray(list)) for (const item of list) if (item !== null && !javaIsBlank(javaValueOf(item))) conditions.push(javaStrip(javaValueOf(item)));
  return conditions;
}

function readRoles(map: ReadonlyMap<string, JavaJson>): SheetRoles {
  const roles: Partial<Record<SheetRole, string>> = {};
  const roleMap = map.get('roles');
  // A role this version doesn't know is skipped.
  if (roleMap instanceof Map) {
    for (const [role, key] of roleMap) {
      const name = role.toUpperCase() as SheetRole;
      if (SHEET_ROLES.includes(name)) roles[name] = javaValueOf(key);
    }
  }
  return roles;
}

function object(json: string | null): Map<string, JavaJson> {
  const map = readJavaJson(javaStrip(json ?? ''));
  if (!(map instanceof Map)) throw new InterchangeError("This file isn't a Realmery character sheet or template.");
  return map;
}

function text(map: ReadonlyMap<string, JavaJson>, key: string): string {
  const value = map.get(key);
  return value === undefined || value === null ? '' : javaValueOf(value);
}
